import { useEffect, useRef } from 'react';
import usePrefersReducedMotion from './usePrefersReducedMotion';

/* Fade and rise an element the first time it comes into view.
   ------------------------------------------------------------------------
   Replaces ScrollReveal, which had a failure mode that loses content rather
   than losing an animation. ScrollReveal measures an element's position once,
   when reveal() is called, and re-measures only on resize. Anything that
   changes the height of the page afterwards leaves those measurements wrong:
   webfonts landing, images sizing themselves, and on this site the experience
   section, which animates its own height whenever a company is selected. Once
   the stored position is wrong the element never satisfies the trigger, and
   because ScrollReveal has already set opacity to 0, the section stays
   invisible for good. That is what made the research section fail to appear on
   narrow screens, where the page above it is tallest and reflows most. Firing
   a resize event by hand brought it straight back, which is what identified
   the cached geometry as the cause.

   IntersectionObserver cannot have that bug: the browser recomputes
   intersections itself whenever layout changes, so there is no stored geometry
   to go stale.

   Two other properties matter more than the animation does.

   The hidden state is applied from JavaScript, after the observer is attached.
   If the script never runs, or IntersectionObserver is missing, nothing is
   ever hidden and the page is simply not animated. Content can no longer be
   lost by an effect failing to fire.

   The trigger is relative to the VIEWPORT, not to the element. ScrollReveal's
   viewFactor asks for a percentage of the element to be visible, which for a
   section several viewports tall is unsatisfiable in principle. Here any part
   of the element crossing the line fires it, whatever its height. */

// Fires once the element is a little way into the viewport rather than the
// instant its first pixel appears.
const ROOT_MARGIN = '0px 0px -8% 0px';

/* The imperative form, for the sections that reveal a list of elements with a
   stagger and therefore cannot call a hook per item. Returns a cleanup
   function, or null when there is nothing to observe. */
export const observeReveal = (el, delay = 0) => {
  if (!el || typeof IntersectionObserver === 'undefined') {
    return null;
  }

  /* An entrance belongs to the first time a page is seen. Once the visitor has
     navigated anywhere, coming back should show the page already settled
     rather than replaying the fade. The flag is set in gatsby-browser.js and
     resets on a real page load, which is when there is no previous page to
     have come from. Carried over from the ScrollReveal wrapper this replaces. */
  if (window.__hasNavigated) {
    return null;
  }

  el.style.transitionDelay = delay ? `${delay}ms` : '';
  el.dataset.reveal = 'hidden';

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        /* Already above the viewport: the reader arrived below it, through a
           deep link or a restored scroll position, so there is no entrance
           left to play. Show it outright rather than leaving it blank until
           they happen to scroll back up. */
        const isAbove = entry.boundingClientRect.bottom <= 0;

        if (!entry.isIntersecting && !isAbove) {
          return;
        }

        el.dataset.reveal = 'shown';
        // One way only: this is an entrance, not a scroll effect.
        observer.disconnect();
      });
    },
    { rootMargin: ROOT_MARGIN },
  );

  observer.observe(el);

  return () => observer.disconnect();
};

/* The hook form, for a section that reveals as one block. Returns a ref to
   put on the element. */
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }
    return observeReveal(ref.current, delay);
  }, [prefersReducedMotion, delay]);

  return ref;
};

export default useReveal;
